import { Box, HStack, PrimaryButton, Stack } from "flicket-ui";
import { ReactNode } from "react";
import { useRouter } from "next/router";
import Skeleton from "react-loading-skeleton";
import { AvailablePaymentMethods } from "@stripe/stripe-js";

import { useExpressCheckoutContext } from "~features/express-checkout/components/ExpressCheckout.context";
import { ExpressCheckoutButtonRow } from "~features/express-checkout/components/ExpressCheckoutButtonRow";
import { addQueryParams } from "~lib/helpers/queryParams";

interface ExpressCheckoutFooterProps {
  priceInfo: ReactNode;
  buttonText: string;
  onAction: (skipRedirect?: boolean) => void;
  isLoading: boolean;
  isDisabled: boolean;
}

export const ExpressCheckoutFooter = ({
  priceInfo,
  buttonText,
  onAction,
  isLoading,
  isDisabled,
}: ExpressCheckoutFooterProps) => {
  const expressCheckoutContext = useExpressCheckoutContext();
  const router = useRouter();

  const loadingExpressCheckout =
    expressCheckoutContext?.isLoadingExpressCheckoutElement;

  const moreThanOnePaymentMethod =
    expressCheckoutContext?.availablePaymentMethods?.length > 1;
  const breakpoint = moreThanOnePaymentMethod ? "xs" : "xxs";

  return (
    <Stack
      width="100%"
      alignItems={{ _: "flex-start", [breakpoint]: "center" }}
      justifyContent={{ _: "flex-start", [breakpoint]: "space-between" }}
      gap={2}
      flexWrap="wrap"
      direction={{ _: "vertical", [breakpoint]: "horizontal" }}
    >
      {priceInfo && <Box flexShrink={0}>{priceInfo}</Box>}

      <Stack
        minWidth={0}
        direction="horizontal"
        gap={1}
        alignItems="flex-end"
        justifyContent="flex-end"
        flex={1}
        width="100%"
        maxW={{ [breakpoint]: "526px" }}
      >
        {loadingExpressCheckout ? (
          <HStack gap={1}>
            <Skeleton height="48px" width="120px" />
            <Skeleton height="48px" width="120px" />
          </HStack>
        ) : (
          <>
            {priceInfo && (
              <PrimaryButton
                flex={1}
                width={{ _: "auto" }}
                maxW={{ [breakpoint]: "160px" }}
                onClick={() => void onAction()}
                disabled={isDisabled}
              >
                {buttonText}
              </PrimaryButton>
            )}

            <ExpressCheckoutButtonRow
              breakpoint={breakpoint}
              onClick={(paymentType: keyof AvailablePaymentMethods) => {
                // Kick off creating the customers order
                onAction(true);

                // Open the modal to show order creation progress
                addQueryParams(router, {
                  modal: "express-checkout",
                  paymentType,
                });
              }}
            />
          </>
        )}
      </Stack>
    </Stack>
  );
};
